import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function AdminTenantsPage() {
  const { t } = useLang();
  const [tenants, setTenants] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tenants");
      setTenants(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setTenants([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (tenant) => {
    const next = tenant.status === "active" ? "inactive" : "active";
    setUpdating(tenant.id);
    try {
      await api.patch(`/admin/tenants/${tenant.id}`, { status: next });
      toast.success(`${tenant.name} ${next === "active" ? "aktifleştirildi" : "pasifleştirildi"}`);
      setTenants((ts) => ts.map((t) => (t.id === tenant.id ? { ...t, status: next } : t)));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="fade-up" data-testid="admin-tenants-page">
      <div className="flex items-center gap-3">
        <Building2 className="h-7 w-7 text-blue-600" />
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Firmalar")}</h1>
      </div>
      <p className="mt-2 text-slate-500">{t("Platforma kayıtlı tüm tenant firmaları. Pasifleştirilen firmaların kullanıcıları giriş yapamaz.")}</p>

      <div className="mt-10 border border-slate-200 bg-white rounded-md overflow-hidden">
        {tenants === null ? (
          <div className="p-12 flex justify-center" data-testid="tenants-loading">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : tenants.length === 0 ? (
          <p className="p-12 text-sm text-slate-500" data-testid="tenants-empty">
            {t("Henüz kayıtlı firma yok. /register-company sayfasından ilk firmayı oluşturabilirsiniz.")}
          </p>
        ) : (
          <Table data-testid="tenants-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("Firma")}</TableHead>
                <TableHead>{t("Vergi No")}</TableHead>
                <TableHead>{t("Admin E-posta")}</TableHead>
                <TableHead>{t("Telefon")}</TableHead>
                <TableHead className="text-center">{t("Kullanıcı")}</TableHead>
                <TableHead>{t("Durum")}</TableHead>
                <TableHead className="text-right">{t("Aktif")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.id} className="hover:bg-slate-50" data-testid={`tenant-row-${tenant.tax_no}`}>
                  <TableCell className="font-medium text-[#0A1128]">{tenant.name}</TableCell>
                  <TableCell className="tabular text-slate-600">{tenant.tax_no}</TableCell>
                  <TableCell className="text-slate-600">{tenant.admin_email}</TableCell>
                  <TableCell className="tabular text-slate-600">{tenant.phone}</TableCell>
                  <TableCell className="text-center tabular text-slate-600">{tenant.user_count}</TableCell>
                  <TableCell>
                    <Badge
                      className={tenant.status === "active"
                        ? "bg-green-100 text-green-800 hover:bg-green-100"
                        : "bg-red-100 text-red-800 hover:bg-red-100"}
                      data-testid={`tenant-status-${tenant.tax_no}`}
                    >
                      {tenant.status === "active" ? t("Aktif") : t("Pasif")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={tenant.status === "active"}
                      disabled={updating === tenant.id}
                      onCheckedChange={() => toggle(tenant)}
                      aria-label={`${tenant.name} aktif durumu`}
                      data-testid={`tenant-toggle-${tenant.tax_no}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
